import { useChatSidebarStore, useChatSidebarToggleSidebar } from '@ui/features/mildang-ai/stores/chatStore';
import { Fab, Stack } from '@mildang/mui';
import { css, cva } from '@mildang/styled-system/css';
import ChatIcon from '@mildang/design-system-legacy/esm/svgs/ChatIcon';
import { Image } from '@mildang/design-system/Image';
import { VisuallyHidden } from 'react-aria';
import { Trans } from '@lingui/react/macro';
import { isRenewalProject } from '@shared/constants/projectTarget';
import { ComputedTotalUnreadCountBadge } from './ComputedTotalUnreadCountBadge';
import { ChannelMemberType } from '@mildang/graphql-artifact';
import chatButtonImage from './assets/chat_button.png';

const buttonStyle = cva({
  base: {
    position: 'relative',
    zIndex: 9999,
    background: 'token(colors.gradient.purpleBlue) !',
    boxShadow:
      '0px 5px 8px -2px rgba(43, 45, 54, 0.16), 0px 1px 4px -1px rgba(43, 45, 54, 0.06), 0px 6px 16px 0px rgba(43, 45, 54, 0.12) !',
    minWidth: '56px',
    minHeight: '56px',
    _before: {
      content: '""',
      opacity: 0,
    },
    _hover: {
      _before: {
        content: '""',
        width: '63px',
        height: '63px',
        borderRadius: '50%',
        background: 'token(colors.gradient.purpleBlue)',
        opacity: 0.3,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: -1,
        transition: 'opacity 0.2s ease-in-out',
      },
    },
  },
  variants: {
    isSidebarOpen: {
      true: {
        marginRight: '360px',
      },
    },
  },
});

// 밀당PT 리뉴얼 버튼. 말풍선 그림 자체가 버튼이다.
const studyButtonStyle = cva({
  base: {
    position: 'relative',
    zIndex: 9999,
    display: 'block',
    p: '0',
    border: 'none',
    bg: 'transparent',
    borderRadius: 'full',
    cursor: 'pointer',
    // DS core.3 그림자(0 4 16)와 같은 값을 그림 모양에 맞춰 drop-shadow로 준다.
    filter: 'drop-shadow(0 token(spacing.4) token(spacing.16) token(colors.neutral.ghostBg.high))',
    _focusVisible: { ring: '2px', ringColor: 'focusRing.border.default' },
  },
  variants: {
    isSidebarOpen: {
      true: {
        marginRight: '360px',
      },
    },
  },
});

const studyImageStyle = css({ display: 'block' });

const studyBadgeStyle = css.raw({ position: 'absolute', top: '0', right: '0' });

export const ChatFloatingButton = ({
  channelMemberType = 'STUDENT',
}: {
  channelMemberType?: ChannelMemberType;
}) => {
  const { isSidebarOpen } = useChatSidebarStore();
  const toggleSidebar = useChatSidebarToggleSidebar();

  const label = (
    <VisuallyHidden>
      <Trans>
        <span>{isSidebarOpen ? '채팅 닫기' : '채팅 열기'}</span>
      </Trans>
    </VisuallyHidden>
  );

  if (isRenewalProject()) {
    return (
      <button type="button" className={studyButtonStyle({ isSidebarOpen })} onClick={() => toggleSidebar()}>
        <Image
          src={chatButtonImage}
          alt=""
          width={56}
          height={56}
          // width만 주면 1x·2x 후보만 생겨 3배 화면에서 흐려진다.
          sizes="56px"
          draggable={false}
          className={studyImageStyle}
        />
        <ComputedTotalUnreadCountBadge channelMemberType={channelMemberType} badgePanda={studyBadgeStyle} />
        {label}
      </button>
    );
  }

  return (
    <Fab
      color="primary"
      size="large"
      key="chat"
      onClick={() => {
        toggleSidebar();
      }}
      className={buttonStyle({ isSidebarOpen })}
    >
      <ComputedTotalUnreadCountBadge
        channelMemberType={channelMemberType}
        badgePanda={css.raw({ position: 'absolute', top: '0px', left: '40px' })}
      />
      <Stack alignItems={'center'}>
        <ChatIcon />
      </Stack>
      {label}
    </Fab>
  );
};
